/**
 * The tank naming convention, on the client (operator ruling 2026-09-14).
 *
 *   `<rack number>.<shelf number><slot letter>`  ->  "2.1A"
 *
 * rack 2, shelf 1 (**shelf 1 is the BOTTOM** — operator ruling
 * 2026-09-15 evening, reversing 2026-09-14; the racks were built
 * bottom-up and the names already said so, so nothing was renamed),
 * slot A — the LEFTMOST position
 * facing the rack (`position_no` 1 -> A, 2 -> B, ...). The position LABEL
 * ("1.3") is untouched: that is the slot WITHIN a rack, and this is the
 * handle you can say anywhere in the building.
 *
 * ⚠️ **THIS IS A PORT, and `facility/services/naming.py` IS THE SOURCE OF
 * TRUTH** — the same relationship `services/tasks_places.py` has with
 * `rackHelpers.wallZoneFor`. It is here because the one caller that needs
 * it has no server round trip to spend: "Add tank to shelf 3" must show a
 * name in the field the moment the editor opens, before a tank exists to
 * ask about. `__tests__/naming.test.js` mirrors the Python suite case for
 * case so the two cannot drift quietly.
 *
 * **NOTHING IS EVER RENAMED AUTOMATICALLY.** Everything here PREFILLS a
 * field the operator can type over, or decides whether to OFFER a rename.
 * Nothing in this file writes.
 */

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * The integer TOKENS of a rack's name; the LAST one is the rack's number.
 *
 * ⚠️ **A DIGIT INSIDE A WORD IS NOT A NUMBER** (Cowork run 5, F8). A bare
 * `/\d{1,4}/g` read "QADELETE 2side rack" as rack 2, so a tank created on
 * that rack was PREFILLED "2.3A" — a name belonging to Rack 2, minted by
 * the app. Written without a lookbehind on purpose: this ships to phones.
 */
const RACK_NUMBER = /(?:^|[^0-9A-Za-z])(\d{1,4})(?![0-9A-Za-z])/g;

/** "2.1A" / "2.A1B" — a NUMBERED rack's tank. */
const NUMBERED = /^(\d{1,4})\.([A-Za-z])?(\d{1,3})([A-Za-z]{1,2})$/;

/** "Tadpole 1A" — a rack whose name carries no number. ONE word of prefix. */
const WORDED = /^([A-Za-z][A-Za-z0-9'’-]*) ([A-Za-z])?(\d{1,3})([A-Za-z]{1,2})$/;

/** What separates the rack from the slot when both are shown. */
export const SLOT_SEPARATOR = ' · ';

/** 1 -> "A", 26 -> "Z", 27 -> "AA". `''` for nothing. */
export function positionLetter(positionNo) {
  let number = Number(positionNo);
  if (!Number.isInteger(number) || number < 1) return '';
  let out = '';
  while (number > 0) {
    const remainder = (number - 1) % 26;
    out = ALPHABET[remainder] + out;
    number = Math.floor((number - 1) / 26);
  }
  return out;
}

/** "A" -> 1, "AA" -> 27. `null` for anything else. The exact inverse. */
export function letterPosition(letter) {
  const text = String(letter || '').trim().toUpperCase();
  if (!text || !/^[A-Z]+$/.test(text)) return null;
  let number = 0;
  for (const char of text) number = number * 26 + (ALPHABET.indexOf(char) + 1);
  return number;
}

/** A rack row OR a bare name, as a string. Callers hold both. */
function rackName(rack) {
  if (typeof rack === 'string') return rack.trim();
  return String((rack && rack.name) || '').trim();
}

/**
 * Is `other` the very row `rack` is? Ids only, never names — a twin
 * answers to the name too. Mirrors `naming._same_rack`.
 */
function sameRack(rack, other) {
  const mine = rackId(rack);
  const theirs = rackId(other);
  return mine !== '' && theirs !== '' && mine === theirs;
}

/** A rack's own id as a string, or `''` for a bare name. */
function rackId(rack) {
  if (!rack || typeof rack === 'string') return '';
  const id = rack.id ?? rack.pk;
  return id == null ? '' : String(id);
}

/**
 * Do BOTH sides carry an id? Then `sameRack` saying no is proof of a
 * twin rather than a caller who handed over bare names.
 * Mirrors `naming._both_identified`.
 */
function bothIdentified(rack, other) {
  return rackId(rack) !== '' && rackId(other) !== '';
}

/**
 * The first four alphanumerics of a rack's id, or `''`.
 *
 * The LAST resort of `rackPrefix`, and only when another live rack
 * answers to the same full name. Mirrors `naming._rack_short_id`.
 */
function rackShortId(rack) {
  return rackId(rack).replace(/[^0-9A-Za-z]/g, '').slice(0, 4);
}

/**
 * The prefix a rack's tanks carry, as `{ prefix, separator }`.
 *
 * The NUMBER in the rack's name takes a dot ("2.1A") — without it "21A" is
 * unreadable. A rack with no number at all uses its FIRST WORD and a space
 * ("Tadpole Rack" -> "Tadpole 1A"), which is the operator's own historical
 * style for the racks that never had a number.
 *
 * ⚠️ **A PREFIX ANOTHER RACK ANSWERS TO IS NOT A PREFIX** (Cowork run 6,
 * D4). Beta's racks are all called `QADELETE <something>` and none of them
 * carries a number, so every one of them prefilled the SAME names — a tank
 * made on `QADELETE 7up rack` opened with "QADELETE 1A" in the field, the
 * name of a live tank on another rack. `others` is the facility's other
 * live racks (rows or names), which the board holds in its tree: when the
 * first word is shared the prefix is the first TWO words, and when that is
 * shared too it is the whole name. With no `others` the answer is the first
 * word, exactly as it was.
 */
export function rackPrefix(rack, others = []) {
  const name = rackName(rack);
  if (!name) return { prefix: '', separator: '' };
  const numbers = [...name.matchAll(RACK_NUMBER)].map((hit) => hit[1]);
  if (numbers.length) {
    return { prefix: String(parseInt(numbers[numbers.length - 1], 10)), separator: '.' };
  }
  const words = name.split(/\s+/);
  const rivals = [];
  let twins = false;
  let sameName = 0;
  (others || []).forEach((other) => {
    const otherName = rackName(other);
    if (!otherName) return;
    // ⚠️ **AN EXACT DUPLICATE IS A RIVAL TOO — BUT THE FIRST ONE IS US**
    // (Cowork run 6 round 2, N2). Two racks genuinely called `QADELETE
    // twin` both prefilled `QADELETE 1A` and no number of words told
    // them apart. Telling "me" from "my twin" is the whole difficulty,
    // and the rule is the same on both sides of the port:
    //   * when BOTH sides carry an id and the ids differ, it is
    //     certainly a twin;
    //   * otherwise `RacksBoard` hands over EVERY name in the tree,
    //     this rack's included, so the FIRST equal name is us and a
    //     SECOND one is a twin.
    // Mirrors `naming._same_rack` / `_both_identified` + the counter.
    if (otherName.toLowerCase() === name.toLowerCase()) {
      if (sameRack(rack, other)) return;
      if (bothIdentified(rack, other)) {
        twins = true;
      } else {
        sameName += 1;
        if (sameName < 2) return;
        twins = true;
      }
      rivals.push(otherName.split(/\s+/).map((word) => word.toLowerCase()));
      return;
    }
    // A NUMBERED rack's tanks carry its number, so its words collide
    // with nothing here.
    if ([...otherName.matchAll(RACK_NUMBER)].length) return;
    rivals.push(otherName.split(/\s+/).map((word) => word.toLowerCase()));
  });
  for (let count = 1; count <= words.length; count += 1) {
    const mine = words.slice(0, count).map((word) => word.toLowerCase());
    const shared = rivals.some(
      (theirs) => theirs.slice(0, count).join(' ') === mine.join(' '),
    );
    if (!shared) return { prefix: words.slice(0, count).join(' '), separator: ' ' };
  }
  // Every word shared: the whole name is all we have, and it is still
  // better than proposing a name that is already taken.
  //
  // ⚠️ **UNLESS ANOTHER RACK ANSWERS TO THE WHOLE NAME** (N2). Two live
  // racks called `QADELETE AI Rack` share every word, so the whole name
  // is not a prefix either and both prefill `QADELETE AI Rack 1A`. The
  // rack's own short id is the only thing left that tells them apart —
  // ugly on purpose, because this is a PREFILL the operator types over
  // and the alternative is proposing a name we know is taken. A bare
  // name carries no id and keeps the whole name, exactly as before.
  if (twins) {
    const short = rackShortId(rack);
    if (short) return { prefix: `${name} ${short}`, separator: ' ' };
  }
  return { prefix: name, separator: ' ' };
}

/**
 * `conventionalName({ rack, shelf, positionNo })` -> "2.1A".
 *
 * `''` when there is nothing to name from — no rack, no shelf, no position,
 * or a rack with no usable name. A FREESTANDING TANK HAS NO CONVENTIONAL
 * NAME and that is the answer, not a gap: the convention is about a slot on
 * a rack, and a tank on a table is not in one.
 *
 * A two-sided rack puts the face between — "2.A1B" — the same face-first
 * order `position_label` uses.
 */
export function conventionalName({ rack, shelf, positionNo, others } = {}) {
  if (!rack || !shelf) return '';
  const { prefix, separator } = rackPrefix(rack, others);
  if (!prefix) return '';
  const letter = positionLetter(positionNo);
  if (!letter) return '';
  const shelfNo = shelf.shelf_no ?? shelf.shelfNo;
  if (shelfNo === null || shelfNo === undefined || shelfNo === '') return '';
  const face = String(shelf.face || '').trim().toUpperCase();
  return `${prefix}${separator}${face}${shelfNo}${letter}`;
}

/**
 * The exact inverse, or `null` for anything that is not the shape.
 *
 * `null` for "Bruce's tank", for "" and for "1.3" (a position LABEL is not
 * a name). Never a partial parse — a caller uses this to decide whether a
 * tank's name belongs to the convention at all, and a half-answer is what
 * offers "Bruce's tank" a rename.
 */
export function parseConventionalName(text) {
  const value = String(text || '').trim();
  if (!value) return null;
  let hit = NUMBERED.exec(value);
  const numbered = Boolean(hit);
  if (!hit) hit = WORDED.exec(value);
  if (!hit) return null;
  const positionNo = letterPosition(hit[4]);
  if (positionNo === null) return null;
  return {
    prefix: numbered ? String(parseInt(hit[1], 10)) : hit[1],
    rackNumber: numbered ? parseInt(hit[1], 10) : null,
    face: (hit[2] || '').toUpperCase(),
    shelfNo: parseInt(hit[3], 10),
    positionNo,
    letter: hit[4].toUpperCase(),
  };
}

/** True when a name belongs to the convention. An EMPTY name is not. */
export function isConventionalName(text) {
  return parseConventionalName(text) !== null;
}

/** Case-insensitive, whitespace-collapsed comparison key for a name. */
export function nameKey(name) {
  return String(name || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * **THE RENAME IS OFFERED, NEVER AUTOMATIC** (operator ruling).
 *
 * True only when the tank's new spot has a conventional name, the tank is
 * not already called it, AND its current name either parses as a
 * conventional name or is empty. **A tank called "Bruce's tank" is never
 * offered a rename** — somebody chose that name, and a move is not a reason
 * to ask them about it again.
 */
export function shouldOfferRename(currentName, wantedName) {
  const want = String(wantedName || '').trim();
  if (!want) return false;
  const current = String(currentName || '').trim();
  if (nameKey(current) === nameKey(want)) return false;
  return current === '' || isConventionalName(current);
}

/**
 * "Rack 2 · 1.3" — the rack name, then the slot. `''` for an unplaced tank.
 *
 * A position label means nothing on its own: every rack in the building has
 * a 1.3. Anywhere a tank is shown OUTSIDE its rack — the Animals grid, the
 * Clutches list, a tank picker, a search hit — the rack is printed with it.
 */
export function rackAndSlot({ rackName, positionLabel } = {}) {
  const label = String(positionLabel || '').trim();
  if (!label) return '';
  const rack = String(rackName || '').trim();
  return rack ? `${rack}${SLOT_SEPARATOR}${label}` : label;
}
